import {
  immutableDate,
  immutableList,
  immutableMap,
  immutableWeakTypedMap,
  primitive,
  serializable
} from '../serialize';
import { IImmutableList, IImmutableMap, IImmutableWeakTypedMap, ImmutableDate } from '../state/redux';

abstract class DiagnosticsContext {
  @serializable(primitive())
  public Identifier!: string;

  @serializable(primitive())
  public Locale!: string;

  @serializable(primitive())
  public TenantId!: string;

  @serializable(primitive())
  public CorrelationId!: string;
}

abstract class DiagnosticsModel extends DiagnosticsContext {
  @serializable(primitive())
  public Name!: string;
  @serializable(primitive())
  public UId!: string
}

export class DiagnosticsInitResponse extends DiagnosticsModel {
  @serializable(immutableWeakTypedMap())
  public JsonSchemaForm!: IImmutableWeakTypedMap;
  @serializable(primitive())
  public BucketId!: number;
  @serializable(primitive())
  public RuleId!: number;
}

export class DiagnosticsExecutionStatus {
  public static StartRequested = 'StartRequested';
  public static Executing = 'Executing';
  public static Completed = 'Completed';
  public static Failed = 'Failed';
  public static ContinueWithNext = 'ContinueWithNext';
}

export enum ErrorCodes {
  None = 'None',
  AttachmentFileSizeError = 'AttachmentFileSizeError',
  AttachmentFileNameError = 'AttachmentFileNameError',
  AttachmentDuplicatedFileNameError = 'AttachmentDuplicatedFileNameError',
  AttachmentFileExtensionError = 'AttachmentFileExtensionError',
  AttachmentInternalServerError = 'AttachmentInternalServerError',
  AttachmentTimedOutError = 'AttachmentTimedOutError',
  DiagnosticsFormRequiredFieldError = 'DiagnosticsFormRequiredFieldError',
  DiagnosticsFormNumberBoundaryError = 'DiagnosticsFormNumberBoundaryError',
  DiagnosticsFormStringLengthError = 'DiagnosticsFormStringLengthError',
  DiagnosticsFormStringPatternError = 'DiagnosticsFormStringPatternError',
  RequestCreateIssueDescriptionError = 'RequestCreateIssueDescriptionError',
  RequestCreateReopenError = 'RequestCreateReopenError'
}

export class ErrorCodeMap {
  @serializable(primitive())
  public Required!: ErrorCodes;

  @serializable(primitive())
  public Pattern!: ErrorCodes;

  @serializable(primitive())
  public NumberBoundary!: ErrorCodes;

  @serializable(primitive())
  public StringLength!: ErrorCodes;

  @serializable(primitive())
  public Date!: ErrorCodes;
}

export class FormConstraint {
  @serializable(primitive())
  public Required!: boolean;

  @serializable(primitive())
  public MaxLength!: number;

  @serializable(primitive())
  public MinLength!: number;

  @serializable(primitive())
  public LowerBound!: number;

  @serializable(primitive())
  public UpperBound!: number;

  @serializable(primitive())
  // tslint:disable-next-line:no-any
  public Pattern!: any;

  @serializable(primitive())
  public Mask!: string;

  @serializable(immutableDate())
  public MinDate!: ImmutableDate;

  @serializable(immutableDate())
  public MaxDate!: ImmutableDate;

  @serializable(immutableMap(ErrorCodeMap))
  public ErrorMap!: IImmutableMap<ErrorCodeMap>;
}

export class FormFieldOption {
  @serializable(primitive())
  public Key!: string;

  @serializable(primitive())
  public LocalizedTitle!: string;

  @serializable(primitive())
  // tslint:disable-next-line no-any
  public Value!: any;
}

export enum EventType {
  Blur = 'blur',
  Change = 'change',
  Click = 'click',
  Focus = 'focus',
  Input = 'input'
}

export enum FormDataTypes {
  String = 'string',
  Number = 'number',
  Boolean = 'boolean',
  Object = 'object'
}

export enum ErrorType {
  None = 'None',
  Required = 'Required',
  NumBoundary = 'NumBoundary',
  StringLength = 'StringLength',
  StringPattern = 'StringPattern'
}

export enum FormFieldType {
  CheckBox = 'CheckBox',
  ChoiceGroup = 'ChoiceGroup',
  ComboBox = 'ComboBox',
  DropDown = 'DropDown',
  DatePicker = 'DatePicker',
  Label = 'Label',
  Link = 'Link',
  TextField = 'TextField',
  Toggle = 'Toggle',
  None = 'None'
}

export class FormField {
  @serializable(primitive())
  public Key!: string;

  @serializable(primitive())
  public Type!: string;

  @serializable(primitive())
  public IsMultilineText!: boolean;

  @serializable(primitive())
  public MultilineRowsCount = 1;

  @serializable(primitive())
  public Title!: string;

  @serializable(primitive())
  // tslint:disable-next-line no-any
  public Default!: any;

  @serializable(primitive())
  // tslint:disable-next-line no-any
  public Value!: any;

  @serializable(primitive())
  public LocalizedToolTip!: string;

  @serializable(primitive())
  public LocalizedPlaceholder!: string;

  @serializable(primitive())
  public LocalizedErrorMessage!: string;

  @serializable(immutableList(immutableMap(FormFieldOption)))
  public AnyOf!: IImmutableList<IImmutableMap<FormFieldOption>>;

  @serializable(immutableList(immutableMap(FormFieldOption)))
  public OneOf!: IImmutableList<IImmutableMap<FormFieldOption>>;

  @serializable(immutableMap(FormConstraint))
  public Constraints!: IImmutableMap<FormConstraint>;

  @serializable(primitive())
  public IsError!: boolean;

  @serializable(primitive())
  public IsDirty!: boolean;
}

export class FormConstraintV2 {
  @serializable(primitive())
  public Required!: boolean;

  @serializable(primitive())
  public MaxLength!: number;

  @serializable(primitive())
  public MinLength!: number;

  @serializable(primitive())
  public LowerBound!: number;

  @serializable(primitive())
  public UpperBound!: number;

  @serializable(primitive())
  // tslint:disable-next-line:no-any
  public Pattern!: any;

  @serializable(primitive())
  public Mask!: string;

  @serializable(immutableDate())
  public MinDate!: ImmutableDate;

  @serializable(immutableDate())
  public MaxDate!: ImmutableDate;

  @serializable(immutableMap(ErrorCodeMap))
  public ErrorMap!: IImmutableMap<ErrorCodeMap>;

  @serializable(immutableList(primitive()))
  public DataType!: IImmutableList<FormDataTypes>;
}

export class Form {
  @serializable(primitive())
  public Key!: string;

  @serializable(primitive())
  public LocalizedTitle!: string;

  @serializable(primitive())
  public LocalizedDescription!: string;

  @serializable(immutableList(immutableMap(FormField)))
  public FormFields!: IImmutableList<IImmutableMap<FormField>>;
}

export class FormFieldOptionV2 {
  @serializable(primitive())
  public Key!: string;

  @serializable(primitive())
  public LocalizedTitle!: string;

  @serializable(primitive())
  // tslint:disable-next-line no-any
  public Value!: any;

  @serializable(immutableMap(FormConstraintV2))
  public OptionConstraints!: IImmutableMap<FormConstraintV2>;
}

export class FormFieldV2 {
  @serializable(primitive())
  public Key!: string;

  @serializable(primitive())
  public Type!: string;

  @serializable(primitive())
  public IsMultilineText!: boolean;

  @serializable(primitive())
  public MultilineRowsCount = 1;

  @serializable(primitive())
  public Title!: string;

  @serializable(primitive())
  // tslint:disable-next-line no-any
  public Default!: any;

  @serializable(primitive())
  // tslint:disable-next-line no-any
  public Value!: any;

  @serializable(primitive())
  public LocalizedToolTip!: string;

  @serializable(primitive())
  public LocalizedPlaceholder!: string;

  @serializable(primitive())
  public LocalizedErrorMessage!: string;

  @serializable(immutableList(immutableMap(FormFieldOptionV2)))
  public AnyOf!: IImmutableList<IImmutableMap<FormFieldOptionV2>>;

  @serializable(immutableList(immutableMap(FormFieldOption)))
  public OneOf!: IImmutableList<IImmutableMap<FormFieldOption>>;

  @serializable(immutableMap(FormConstraintV2))
  public Constraints!: IImmutableMap<FormConstraintV2>;

  @serializable(primitive())
  public IsError!: boolean;

  @serializable(primitive())
  public IsDirty!: boolean;
}

export enum SAFActionProvider {
  SAF = 'SAF',
  KnowMe = 'KnowMe'
}

export enum SAFParamProvider {
  Context = 'Context',
  GlobalVariables = 'GlobalVariables'
}

export class  SAFContextVariable {
  public Name: string;
  public ValueProvider: SAFParamProvider;
}

export class FormV2 {
  @serializable(primitive())
  public Key!: string;

  @serializable(primitive())
  public LocalizedTitle!: string;

  @serializable(primitive())
  public LocalizedDescription!: string;

  @serializable(immutableList(immutableMap(FormFieldV2)))
  public FormFields!: IImmutableList<IImmutableMap<FormFieldV2>>;

  @serializable(immutableWeakTypedMap())
  public JsonFormValues!: IImmutableWeakTypedMap;

  @serializable(primitive())
  public ButtonLabel!: string;

  @serializable(primitive())
  public CorrelationId!: string;

  @serializable(primitive())
  public Identifier!: string;

  @serializable(primitive())
  public Locale!: string;

  @serializable(primitive())
  public TenantId!: string;

  @serializable(immutableList(immutableMap(SAFContextVariable)))
  public ContextVariables!: IImmutableList<IImmutableMap<SAFContextVariable>>;

  @serializable(immutableList(primitive()))
  public GlobalVariables!: IImmutableList<string>;

  @serializable(primitive())
  public ActionProvider!: SAFActionProvider;
}

export enum ParameterValueProvider{
  context=  'context',
  globalVariables = 'globalvariables',
  userInput = 'userinput'
}

export class JsonSchemaConstants {
  public static Action = 'action';
  public static AnyOf = 'anyOf';
  public static Default = 'default';
  public static Description = 'description';
  public static Enum = 'enum';
  public static Format = 'format';
  public static Hidden = 'hidden';
  public static MaxLength = 'maxLength';
  public static MinLength = 'minLength';
  public static MultiLineCount = 'multiLineCount';
  public static OneOf = 'oneOf';
  public static Pattern = 'pattern';
  public static PlaceHolder = 'placeholder';
  public static Properties = 'properties';
  public static Required = 'required';
  public static Title = 'title';
  public static ToolTip = 'tooltip';
  public static Type = 'type';
  public static Value = 'value';
  public static ValueProvider = 'valueProvider';
}

export const DiagnosticsParamTypeToFieldTypeMap: { [index: string]: string } = {
  EmailAddress: FormFieldType.TextField,
  DateTime: FormFieldType.DatePicker,
  MultilineText: FormFieldType.TextField,
  Text: FormFieldType.TextField,
  CheckMark: FormFieldType.CheckBox,
  DropDown: FormFieldType.DropDown,
  Uri: FormFieldType.TextField,
  DomainName: FormFieldType.None,
  NotUserInput: FormFieldType.None
};

export class SafConstants {
  public static DiagnosticsMultilineTextFieldRowCount = 8;
  public static DiagnosticsTitleStringLimit = 92;
  public static DiagnosticsButtonLabelLimit = 62;
  public static DiagnosticsDatePickerMinDateThreshold = -7;
  public static InlineDiagnosticsMaxRetriesCount = 15;
  public static SAFDiagnosticsMaxRetriesCount = 3;
  public static InlineDiagnosticsServiceTimerDelayInMilliseconds = 12000;
}

export class DiagnosticsProgressRequest extends DiagnosticsContext {
  @serializable(primitive())
  public SessionId!: string;
}

export class Timer {
  public Delay: number;
  public MaxIterations: number;
  // Interval ID of the timer. Pass it to window.clearInterval() when cancel the timer.
  public IntervalId?: number = undefined;

  constructor(delay: number, maxIterations: number) {
    this.Delay = delay;
    this.MaxIterations = maxIterations;
  }
}

export class DiagnosticsProgressResponse extends DiagnosticsModel {
  @serializable(primitive())
  public SessionId!: string;

  @serializable(primitive())
  public Status!: string;

  @serializable(primitive())
  public HttpStatusCode!: number;

  //  Error Message
  @serializable(primitive())
  public Message!: string;

  @serializable(primitive())
  public Title!: string;

  @serializable(primitive())
  public Details!: string;

  @serializable(immutableList(immutableMap(DiagnosticsInitResponse)))
  public Next!: IImmutableList<IImmutableMap<DiagnosticsInitResponse>>;

  @serializable(primitive())
  public AbandonCurrentRun = false;
}

export class DiagnosticsStartRequest extends DiagnosticsContext {
  @serializable(primitive())
  public JsonFormValues!: string;
}

export class SAFActionSchemaResponse{ 
  public  JsonSchemaForm!: object;
} 

export class SAFActionExecutionRequestPayload{
  @serializable(immutableWeakTypedMap())
  public Parameters!: string;
}

export class SAFActionExecutionResponse  {
  public statusCode!: string;
  public response!: string;
  public callingServiceCorrelationVector!: string;
  public context!: object;
  public errorMessage!: string;
}

export class ISAFActionExecutionResponseV2 {
  public StatusCode!: number;
  public Headers!: object;
  public Value!: any;
}
export type ISAFActionExecutionResponse = SAFActionExecutionResponse & ISAFActionExecutionResponseV2;


